import { isoDateTime, z } from "../zod.js";
import { Locale, Uuid } from "../identity/admin-schemas.js";
import { VERTICAL_PACK_KEYS } from "./schemas.js";

// Module 15 — plans, entitlements, tenant edits and usage (M15-6 and M15-7a;
// spec 15 §2.7 to §2.10, §2.16, §2.17, §3.3, §5; D-486, D-487, D-496,
// D-615 to D-619). Every path answers only on the admin host behind the staff
// cookie, like the rest of /v1/platform/*.

/**
 * The module codes a plan can switch on (D-486 ruling 5, D-616 ruling 1): the
 * shipped modules, each joining with its first surface, plus one
 * `vertical_pack:<key>` per pack the console can create a tenant with. A
 * code that is not here is refused at the API (422 MODULE_CODE_UNKNOWN), so a
 * typo cannot become a plan row nobody reads. The pilot plan (migration 0114)
 * turns every one of them on.
 */
export const ENTITLEMENT_FEATURE_CODES = [
  "catalogue",
  "onboarding",
  "inventory",
  "waste",
  "production",
  "quality",
  "planning",
  "sales",
  "messaging",
  "agents",
] as const;

export type EntitlementFeatureCode = (typeof ENTITLEMENT_FEATURE_CODES)[number];

export function verticalPackModuleCode(pack: string): string {
  return `vertical_pack:${pack}`;
}

export const ENTITLEMENT_MODULE_CODES: readonly string[] = [
  ...ENTITLEMENT_FEATURE_CODES,
  ...VERTICAL_PACK_KEYS.map(verticalPackModuleCode),
];

/** The limit codes with a consumer (D-486 ruling 6, migration 0114's CHECK). */
export const PlanLimitCode = z
  .enum(["max_facilities", "max_users_soft", "storage_gb", "api_requests_day"])
  .openapi("PlanLimitCode");
export type PlanLimitCode = z.infer<typeof PlanLimitCode>;

export const LimitEnforcement = z
  .enum(["hard", "soft", "fair_use", "soft_then_hard"])
  .openapi("LimitEnforcement");

/** Each code's one enforcement (spec 15 §2.9's canonical list). */
export const CANONICAL_ENFORCEMENT: Readonly<
  Record<PlanLimitCode, z.infer<typeof LimitEnforcement>>
> = {
  max_facilities: "hard",
  max_users_soft: "soft",
  storage_gb: "soft_then_hard",
  api_requests_day: "hard",
};

/** The seeded plan every tenant without a plan row is on, and the one a
 * create with no plan_id is given (D-615 ruling 1). It is never retired
 * (409 PILOT_PLAN_REQUIRED), and no other plan can take its code. */
export const PILOT_PLAN_CODE = "pilot";

export const PlanStatus = z.enum(["active", "grandfathered", "retired"]).openapi("PlanStatus");

const PlanCode = z
  .string()
  .min(2)
  .max(40)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "lower-case letters, digits and inner hyphens");

const ModuleCode = z
  .string()
  .min(1)
  .max(80)
  .regex(/^(?:[a-z][a-z_]*|vertical_pack:[a-z0-9]+(?:-[a-z0-9]+)*)$/);

const Currency = z.string().regex(/^[A-Z]{3}$/);

export const PlatformPlanEntitlement = z
  .object({ module_code: z.string(), enabled: z.boolean() })
  .openapi("PlatformPlanEntitlement");

export const PlatformPlanLimit = z
  .object({
    limit_code: PlanLimitCode,
    limit_value: z.number().int().min(0),
    enforcement: LimitEnforcement,
    warn_pct: z.number().int().nullable(),
    block_pct: z.number().int().nullable(),
  })
  .openapi("PlatformPlanLimit");

export const PlatformPlanView = z
  .object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    status: PlanStatus,
    /** Null for platform_support and platform_readonly (spec 15 §7, D-486
     * ruling 3): the API masks it, never the browser. */
    annual_price_minor: z.number().int().min(0).nullable(),
    currency: z.string(),
    dunning_suspend_after_days: z.number().int(),
    notes: z.string().nullable(),
    /** Sandbox staff may edit a sandbox plan, and only a sandbox tenant may
     * be put on one (D-615 ruling 3). */
    sandbox: z.boolean(),
    tenant_count: z.number().int().min(0),
    entitlements: z.array(PlatformPlanEntitlement),
    limits: z.array(PlatformPlanLimit),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("PlatformPlanView");

export const PlatformPlanListResponse = z
  .object({ items: z.array(PlatformPlanView) })
  .openapi("PlatformPlanListResponse");

const PlanEntitlementInput = z.object({ module_code: ModuleCode, enabled: z.boolean() });

const PlanLimitInput = z.object({
  limit_code: PlanLimitCode,
  limit_value: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  /** Required for storage_gb (soft_then_hard) and refused for every other
   * code (422 LIMIT_THRESHOLDS_INVALID). */
  warn_pct: z.number().int().min(1).max(1000).optional(),
  block_pct: z.number().int().min(2).max(1000).optional(),
});

export const PlatformPlanCreateRequest = z
  .object({
    code: PlanCode,
    name: z.string().min(1).max(200),
    annual_price_minor: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    currency: Currency,
    dunning_suspend_after_days: z.number().int().min(7).max(90).optional(),
    notes: z.string().max(2000).optional(),
    /** Each code at most once. A code not listed is off. */
    entitlements: z.array(PlanEntitlementInput).max(100),
    /** Each code at most once. A code not listed has no limit row. */
    limits: z.array(PlanLimitInput).max(10),
  })
  .openapi("PlatformPlanCreateRequest");

export const PlatformPlanUpdateRequest = z
  .object({
    name: z.string().min(1).max(200).optional(),
    annual_price_minor: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).optional(),
    currency: Currency.optional(),
    dunning_suspend_after_days: z.number().int().min(7).max(90).optional(),
    notes: z.string().max(2000).nullable().optional(),
    /** Rows to set: each listed code takes the given value; unlisted codes
     * keep theirs. */
    entitlements: z.array(PlanEntitlementInput).max(100).optional(),
    limits: z.array(PlanLimitInput).max(10).optional(),
  })
  .openapi("PlatformPlanUpdateRequest");

// --- a tenant's effective entitlements (spec 15 §3.4, S6) ---

export const PlatformEntitlementOverride = z
  .object({
    enabled: z.boolean().nullable(),
    limit_value: z.number().int().nullable(),
    reason: z.string(),
    expires_at: isoDateTime().nullable(),
    created_by_staff_id: Uuid,
    updated_at: isoDateTime(),
  })
  .openapi("PlatformEntitlementOverride");

export const PlatformModuleEntitlement = z
  .object({
    code: z.string(),
    plan: z.boolean(),
    override: PlatformEntitlementOverride.nullable(),
    effective: z.boolean(),
  })
  .openapi("PlatformModuleEntitlement");

export const PlatformLimitUsage = z
  .object({ value: z.number().int().min(0), unit: z.enum(["count", "bytes"]) })
  .openapi("PlatformLimitUsage");

export const PlatformLimitEntitlement = z
  .object({
    code: PlanLimitCode,
    enforcement: LimitEnforcement,
    /** Null when the plan has no row for the code. */
    plan: z.number().int().nullable(),
    override: PlatformEntitlementOverride.nullable(),
    /** The value that applies: the live override's, else the plan's; null
     * when neither names one, which means no limit. */
    effective: z.number().int().nullable(),
    warn_pct: z.number().int().nullable(),
    block_pct: z.number().int().nullable(),
    /** The tenant's current use where its owner module answers it (D-496):
     * facilities from module 16, members from module 16, stored bytes from
     * the files layer; null for a code with no source (api_requests_day). */
    usage: PlatformLimitUsage.nullable(),
  })
  .openapi("PlatformLimitEntitlement");

export const PlatformPlanRef = z
  .object({ id: Uuid, code: z.string(), name: z.string(), status: PlanStatus })
  .openapi("PlatformPlanRef");

export const PlatformTenantEntitlementsResponse = z
  .object({
    tenant_id: Uuid,
    status: z.string(),
    plan: PlatformPlanRef,
    modules: z.array(PlatformModuleEntitlement),
    limits: z.array(PlatformLimitEntitlement),
  })
  .openapi("PlatformTenantEntitlementsResponse");

export const EntitlementOverrideKind = z
  .enum(["module", "limit"])
  .openapi("EntitlementOverrideKind");

export const PlatformOverrideSetRequest = z
  .object({
    /** For kind=module. */
    enabled: z.boolean().optional(),
    /** For kind=limit. */
    limit_value: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).optional(),
    reason: z.string().trim().min(5).max(2000),
    /** Must be in the future. Absent: the override stands until removed. */
    expires_at: isoDateTime().optional(),
  })
  .openapi("PlatformOverrideSetRequest");

export const PlatformOverrideRemoveRequest = z
  .object({ reason: z.string().trim().min(5).max(2000) })
  .openapi("PlatformOverrideRemoveRequest");

// --- tenant edits (spec 15 §3.3, §5 PATCH and subdomain) ---

export const PlatformTenantUpdateRequest = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    /** A move to another plan (spec 15 §3.3). Downgrading below the
     * tenant's facility count answers 409 LIMIT_BELOW_USAGE unless force. */
    plan_id: Uuid.optional(),
    force: z.boolean().optional(),
    default_locale: Locale.optional(),
    /** An IANA zone the runtime knows; a refinement, so the published
     * schema stays a plain string. */
    default_timezone: z
      .string()
      .min(1)
      .max(64)
      .superRefine((zone, ctx) => {
        if (!isIanaZone(zone)) {
          ctx.addIssue({ code: "custom", message: "not an IANA time zone" });
        }
      })
      .optional(),
  })
  // Strict: `currency` is never a field here (spec 16 §2.1 locks it), and a
  // body that names it is refused rather than silently stripped.
  .strict()
  .openapi("PlatformTenantUpdateRequest");

/** A time zone the runtime's IANA list knows. */
export function isIanaZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export const PlatformSubdomainRenameRequest = z
  .object({
    new_subdomain: z
      .string()
      .min(3)
      .max(63)
      .regex(/^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/),
    reason: z.string().trim().min(5).max(2000),
  })
  .openapi("PlatformSubdomainRenameRequest");

// --- usage (spec 15 §2.16, §2.17, S7) ---

export const UsageMetric = z
  .enum(["active_users", "domain_events", "storage_bytes"])
  .openapi("UsageMetric");

export const PlatformUsagePoint = z
  .object({
    date: z.iso.date(),
    /** Null when the day has no reading (spec 15 §9.14: missing, not 0). */
    value: z.number().int().min(0).nullable(),
  })
  .openapi("PlatformUsagePoint");

export const PlatformUsageSeries = z
  .object({ metric: UsageMetric, points: z.array(PlatformUsagePoint) })
  .openapi("PlatformUsageSeries");

export const PlatformUsageMonth = z
  .object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    mau: z.number().int().min(0),
    domain_events: z.number().int().min(0),
    /** Null when no storage reading was taken that month (every read failed):
     * a peak never measured is not 0 (D-617 ruling 3). */
    storage_bytes_peak: z.number().int().min(0).nullable(),
    refreshed_at: isoDateTime(),
  })
  .openapi("PlatformUsageMonth");

export const PlatformTenantUsageResponse = z
  .object({
    tenant_id: Uuid,
    month: z.string().regex(/^\d{4}-\d{2}$/),
    /** The daily series: the 90 UTC days ending on the month's last day,
     * or today when the month is the current one. */
    from: z.iso.date(),
    to: z.iso.date(),
    series: z.array(PlatformUsageSeries),
    /** The month's rollup, null until the metering worker has written one. */
    monthly: PlatformUsageMonth.nullable(),
  })
  .openapi("PlatformTenantUsageResponse");

/** Problem codes M15-6 and M15-7a add (D-615 to D-619). */
export const platformEntitlementProblemCodes = [
  "PLAN_NOT_FOUND",
  "PLAN_CODE_TAKEN",
  "PLAN_RETIRED",
  "PLAN_SANDBOX_ONLY",
  "PLAN_UNKNOWN",
  "PACK_NOT_IN_PLAN",
  "MODULE_CODE_UNKNOWN",
  "LIMIT_THRESHOLDS_INVALID",
  "LIMIT_BELOW_USAGE",
  "OVERRIDE_INVALID",
  "OVERRIDE_NOT_FOUND",
  "PILOT_PLAN_REQUIRED",
] as const;
